import React, { useState, useEffect } from 'react';
import { NavLink } from "react-router-dom";
import { User } from '../models/user';
import { UserGroup } from '../models/user-group';
import { Role } from '../models/role';
import { MemberRole } from '../models/member-role';
import { UserGroupUser } from '../models/user-group-user';
import { RoleLevelType } from '../models/enums';
import { ToastProps } from '../models/toast-props';
import AddUsersComponent from './AddUsersComponent';
import { AuthConstants } from '../store/auth-constants';
import { CrudFunctions } from '../utilities/crud-functions';
import { UserDataStore } from '../store/userDataStore';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import DmaFooter from './DmaFooter';

import DataGrid, {
    Column,
    FilterRow,
    Selection,
    Popup,
    HeaderFilter,
    Editing,
    Paging,
    Form,
    Toolbar,
    Item as ToolBarItem,
    Button as ColumnButton,
    LoadPanel
} from 'devextreme-react/data-grid';

import { SelectBox, List, TextArea } from 'devextreme-react';
import { Item } from 'devextreme-react/form';
import { Button } from 'devextreme-react/button';
import { Toast } from 'devextreme-react/toast';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import Guid from 'devextreme/core/guid';
import DataSource from 'devextreme/data/data_source';
import { DocumentationConstants } from '../store/documentation-constants';

export default function UsersForm() {
    const [users, setUsers] =
        useState<User[]>();

    const [userGroups, setUserGroups] =
        useState<UserGroup[]>();

    const [userGroupUsers, setUserGroupUsers] =
        useState<UserGroupUser[]>();

    const [roles, setRoles] =
        useState<Role[]>();

    const [roleMembers, setRoleMembers] =
        useState<MemberRole[]>();

    const [selectedUser, setSelectedUser] =
        useState<User>();

    const [selectedUserGroup, setSelectedUserGroup] =
        useState<UserGroup>();

    const [selectedUserGroupMember, setSelectedUserGroupMember] =
        useState<User>();

    const [selectedRole, setSelectedRole] =
        useState<Role>();

    const [selectedRoleMember, setSelectedRoleMember] =
        useState<MemberRole>();

    const [selectedRoleLevel, setSelectedRoleLevel] =
        useState<RoleLevelType>();

    const [userPermissionInformation, setUserPermissionInformation] =
        useState<string>();

    const [roleLevelDialogVisible, setRoleLevelDialogVisible] =
        useState<boolean>();

    const [quickAddNewUsersDialogVisible, setQuickAddNewUsersDialogVisible] =
        useState<boolean>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();
            
    useEffect(() => {
        getAllData();
    }, []);

    const userGroupUsersDataSource = new DataSource({
        store: userGroupUsers ? userGroupUsers : [],
        paginate: false
    });

    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    return (
        <div className="pw-form-container-2">
            <div className="lq-title-bar">
                <div className="lq-main-title">Users Management</div>
                <div className="title-bar-spacer" />
                <div className="save-toolbar">
                    <div>
                        <Button className="title-bar-button"
                            text="" icon="tel" type="normal"
                            hint="report issue"
                            onClick={showTicketLogEntryDialog} />
                    </div>
                    <div>
                        <NavLink to={`/documentation/${DocumentationConstants.UsersManagementForm}`}
                            className="pw-right-align-content" target="_blank" rel="noopener noreferrer">
                            <Button className="title-bar-button pw-orange"
                                text="" icon="help" type="normal"
                                hint="help and documentation" />
                        </NavLink>
                    </div>
                    <div>
                        <Button className="title-bar-button"
                            text="" icon="arrowleft" type="normal"
                            hint="go back"
                            onClick={goBack} />
                    </div>
                </div>
            </div>

            <div className="pw-comp-row">
                <DataGrid height="24em"
                    keyExpr="userId"
                    dataSource={users ? users : []}
                    onSelectionChanged={userSelectionChanged}
                    onRowValidating={onUserValidating}
                    onInitNewRow={onInitNewUserRow}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true}>

                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />
                    <Selection mode={'single'} />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />

                    <Editing
                        mode="popup"
                        useIcons={true}
                        allowAdding={canEdit}
                        allowDeleting={false}
                        allowUpdating={canEdit}>

                        <Popup title="User" showTitle={true} width='35rem' height='23rem' />
                        <Form>
                            <Item dataField="userName" colSpan={2} />
                            <Item dataField="domainUserName" colSpan={2} />
                            <Item dataField="lastName" colSpan={2} />
                            <Item dataField="firstName" colSpan={2} />
                            <Item dataField="email" colSpan={2} />
                            <Item dataField="active" colSpan={2} />
                            {/*
                            <Item dataField="roleList" colSpan={2} disabled
                                editorOptions={{ height: 100 }}
                                editorType="dxTextArea" />
                                */}
                        </Form>
                    </Editing>

                    <Column type="buttons" width='4rem'>
                        <ColumnButton name="edit" disabled={!canEdit} />
                        {/*
                        <ColumnButton hint="add to user group" icon="group" disabled={!canEdit}
                            onClick={onAddUserToGroupButtonClicked} />
                            */}
                    </Column>

                    <Column dataField={'userName'} width={'20%'} />
                    <Column dataField={'domainUserName'} width={'20%'} />
                    <Column dataField={'lastName'} width={'10%'} />
                    <Column dataField={'firstName'} width={'10%'} />
                    <Column dataField={'email'} cellRender={emailCellRender} width={'28%'} />
                    <Column dataField={'active'} width={'12%'} />
                    <Column dataField={'roleList'} />

                    <Toolbar>
                        <ToolBarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Users</div>
                            </div>
                        </ToolBarItem>


                        <ToolBarItem location='after' name="addRowButton" disabled={!canEdit} />

                        {/*
                        <ToolBarItem location='after'>
                            <Button type='normal' icon="importselected"
                                width={'2.1em'} hint="quick add new users"
                                onClick={onQuickAddNewUsers}>
                            </Button>
                        </ToolBarItem>
                        */}

                        <ToolBarItem location='after'>
                            <Button type='normal' icon="pulldown"
                                width={'2.1em'} hint="reload server"
                                onClick={onReloadUsersAtServerClick}>
                            </Button>
                        </ToolBarItem>
                    </Toolbar>

                </DataGrid>
            </div>

            <div className="pw-margin-top-05 pw-margin-bottom-1">
                <div className="pw-form-unit-label">User Permissions</div>
                <TextArea id="specialHandlingInstructions"
                    height="3.5rem" placeholder='NA' readOnly={true}
                    value={userPermissionInformation ? userPermissionInformation : ''} >
                </TextArea>
            </div>

            <div className="pw-left-grow-2 pw-margin-bottom-05">
                <div>

                    <DataGrid
                        height="22.3em"
                        dataSource={userGroups ? userGroups : []}
                        onSelectionChanged={userGroupSelectionChanged}
                        onRowValidating={onUserGroupValidating}
                        onRowRemoving={onUserGroupDeleting}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showBorders={true}>

                        <LoadPanel enabled={true} />
                        <Paging enabled={false} />
                        <Selection mode={'single'} />

                        <Column type="buttons" width='4rem'>
                            <ColumnButton name="edit" disabled={!canEdit} />
                            <ColumnButton hint="add role to user group"
                                icon="insertrowbelow" disabled={!canEdit}
                                onClick={onUserGroupAddToRoleButtonClicked} />
                        </Column>

                        <Column dataField={'groupName'} width={'30%'} />
                        <Column dataField={'description'} width={'70%'} />

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">User Groups</div>
                                </div>
                            </ToolBarItem>
                            <ToolBarItem location='after' name="addRowButton" disabled={!canEdit} />
                        </Toolbar>
                                                
                        <Editing
                            mode="popup"
                            useIcons={true}
                            allowAdding={canEdit}
                            allowDeleting={false}
                            allowUpdating={canEdit}>

                            <Popup title="User Group" showTitle={true} width={700} height={250} />
                            <Form>
                                <Item dataField="groupName" colSpan={2} />
                                <Item dataField="description" colSpan={2} />
                            </Form>
                        </Editing>
                    </DataGrid>
                </div>

                <div className="pw-comp-20">
                    <div className="pw-list-title-buttons-2">
                        <div className="pw-list-title">User Group Members</div>
                        <div>
                            <Button id="userGroupsAddButton" text="" icon="user"
                                type="normal" disabled={!canEdit}
                                hint="add user to group"
                                onClick={onAddUserToUserGroup} />
                        </div>
                        <div>
                            <Button id="removeUserFromGroupButton" text="" icon="return" type="normal"
                                hint="remove user from group" disabled={!canEdit}
                                onClick={removeUserFromGroup} />
                        </div>
                    </div>

                    <List height="18em"
                        className="dx-listbox"
                        onItemClick={onUserGroupMembersItemClick}
                        itemRender={userGroupUsersRender}
                        dataSource={userGroupUsersDataSource} />

                </div>
            </div>

            {/*
            <div className="pw-left-grow-2 pw-margin-bottom-05">
                <div>
                    <DataGrid
                        height="22.3em"
                        dataSource={roles ? roles : []}
                        onSelectionChanged={rolesSelectionChanged}
                        onRowValidating={rolesValidating}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showBorders={true}>

                        <LoadPanel enabled={true} />
                        <Paging enabled={false} />
                        <Selection mode={'single'} />

                        <Column type="buttons" width='4rem'>
                            <ColumnButton name="edit" disabled={!canEdit} />
                            <ColumnButton hint="add role to user group"
                                icon="insertrowbelow" disabled={!canEdit}
                                onClick={onAddRoleToUserGroupButtonClicked} />
                        </Column>

                        <Column dataField={'roleName'} width={'30%'} />
                        <Column dataField={'description'} width={'70%'} />

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Roles</div>
                                </div>
                            </ToolBarItem>
                        </Toolbar>

                        <Editing
                            mode="popup"
                            useIcons={true}
                            allowAdding={canEdit}
                            allowDeleting={false}
                            allowUpdating={canEdit}>

                            <Popup title="Role" showTitle={true} width={700} height={250} />
                            <Form>
                                <Item dataField="roleName" colSpan={2} />
                                <Item dataField="description" colSpan={2} />
                            </Form>

                        </Editing>
                    </DataGrid>
                </div>

                <div className="pw-comp-20">
                    <div className="pw-list-title-buttons-2">
                        <div className="pw-list-title">Role Members</div>
                        <div>
                            <Button id="rolesAddGroupButton" text="" icon="group"
                                type="normal" disabled={!canEdit}
                                hint="add group to role"
                                onClick={onUserGroupAddToRoleButtonClicked} />
                        </div>
                        <div>
                            <Button id="removeRoleMemberButton" text="" icon="trash" type="normal"
                                hint="remove member from role" disabled={!canEdit}
                                onClick={removeMemberFromRole} />
                        </div>
                    </div>

                    <List height="18em" className="dx-listbox"
                        onItemClick={onRoleMembersItemClick}
                        itemRender={roleMembersRender}
                        dataSource={roleMembers ? roleMembers : []} />
                </div>
            </div>
            */}


            <DmaFooter />

            {roleLevelDialogVisible &&
                <DialogPopup
                    visible={roleLevelDialogVisible}
                    onHiding={hideRoleLevelDialog}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showTitle={true}
                    title="Assign Role"
                    width="24rem"
                    height="16rem">

                    <div className="pw-comp-row">
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Role</div>
                            <div className="pw-readonly-label">{selectedRole ? selectedRole.roleName : ''}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Assigned To User Group</div>
                            <div className="pw-readonly-label">{selectedUserGroup ?
                                selectedUserGroup.groupName : ''}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Role Level</div>
                            <SelectBox id="RoleLevelsSelect" className="selectbox"
                                placeholder='NA'
                                displayExpr='name' valueExpr='id'
                                dataSource={AuthConstants.RoleLevels ?
                                    AuthConstants.RoleLevels : []}
                                onValueChange={onRoleLevelSelectionChanged}
                                value={selectedRoleLevel ? selectedRoleLevel : undefined} />
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-comp-row">
                            <div className="pw-centered-buttons-container">
                                <Button type="success" id="addNewBatchDialogSaveButton" className="pw-button" onClick={onAddMemberToRole} text="Save" />
                                <Button className="pw-button" id="addNewBatchDialogCancelButton" onClick={hideRoleLevelDialog} text="Cancel" />
                            </div>
                        </div>
                    </div>

                </DialogPopup>
            }

            {quickAddNewUsersDialogVisible &&
                <DialogPopup
                    visible={quickAddNewUsersDialogVisible}
                    onHiding={hideQuickAddNewUsersDialog}
                    dragEnabled={true}
                    fullScreen={true}
                    hideOnOutsideClick={false}
                    title="Add New Users">
                    <AddUsersComponent users={users}
                        userGroups={userGroups}
                        onClose={onCloseAddUsersDialog} />
                </DialogPopup>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    function onInitNewUserRow(e: any) {
        let newUser: User = {
            userId: '',
            userName: '',
            domainUserName: '',
            active: true
        };

        e.data = newUser;
    }

    function onQuickAddNewUsers() {
        showQuickAddNewUsersDialog();
    }

    function onReloadUsersAtServerClick() {
        reloadUsersAtServer();
    }

    function onCloseAddUsersDialog() {
        hideQuickAddNewUsersDialog();
    }

    function onAddUserToGroupButtonClicked(e: any) {
        console.log("onAddUserToGroupButtonClicked");
        console.log(e);

    }

    //button on User Groups form
    function onUserGroupAddToRoleButtonClicked(e: any) {
        console.log("onUserGroupAddToRoleButtonClicked");
        console.log(e);

        if (selectedRole && selectedUserGroup) {
            showRoleLevelDialog();
        }
        else {
            const message = "You must first select a User Group and a Role";
            showInformationMessage(ModalMessageType.Information, "Missing Information", message);
        }
    }

    //button on Roles form
    function onAddRoleToUserGroupButtonClicked(e: any) {
        console.log("onAddRoleToUserGroupButtonClicked");
        console.log(e);

        if (selectedRole && selectedUserGroup) {
            showRoleLevelDialog();
        }
        else {
            const message = "You must first select a User Group and a Role";
            showInformationMessage(ModalMessageType.Information, "Missing Information", message);
        }
    }

    function onRoleLevelSelectionChanged(e: any) {
        console.log("onRoleLevelSelectionChanged");
        console.log(e);

        //todo is this right?
        setSelectedRole(e);
    }

    //change functions 

    function userSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            let user = e.selectedRowsData[0] as User;

            console.log("selected user..")
            console.log(user)


            setSelectedUser(user);
            setUserPermissionInformation(`Group Codes: ${user.roleCodes.toString()}\nRoles: ${user.roleList}`);
        }
    }

    function userGroupSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            getUserGroupUsers(e.selectedRowsData[0].userGroupId);

        setSelectedUserGroup(e.selectedRowsData[0]);
        setSelectedUserGroupMember(null);
    }

    function rolesSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            getRoleMembers(e.selectedRowsData[0].roleId);

        setSelectedRole(e.selectedRowsData[0]);
        setSelectedRoleMember(null);
    }

    function onUserGroupMembersItemClick(e: any) {
        setSelectedUserGroupMember(e.itemData);
    }

    function onRoleMembersItemClick(e: any) {
        setSelectedRoleMember(e.itemData);
    }

    //add functions

    /*
    onAddGroupToRole() {
        if (this.state.selectedUserGroup && this.state.selectedUserGroup.userGroupId && this.dataLoaded) {

            this.onAddMemberToRole(this.state.selectedUserGroup.userGroupId);
        }
    }
    */

    /*
     async onAddMemberToRoleOLD(memberId: string) {
         if (this.state.selectedRole && this.state.selectedRole.roleId) {
             let memberRole: MemberRole = {
                 memberId: memberId,
                 roleId: this.state.selectedRole.roleId,
                 roleLevel: 0
             };
 
             this.addMemberToRole(memberRole)
                 .then(() => {
                     if (this.state.selectedRole) {
                         this.getRoleMembers(this.state.selectedRole.roleId)
                             .catch((response: ResponseError) => {
                                 XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: response });
                             });
                     }
                 })
                 .catch((response: ResponseError) => {
                     XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: response });
                 });
         }
     }
     */

    async function onAddMemberToRole() {
        if (selectedRole && selectedUserGroup &&
            selectedRoleLevel) {

            const memberRole: MemberRole = {
                roleId: selectedRole.roleId,
                memberId: selectedUserGroup.userGroupId,
                roleLevel: selectedRoleLevel
            };

            await addMemberToRole(memberRole)
                .then(() => {
                    showToast("Member added to role", "success");

                    if (selectedRole)
                        getRoleMembers(selectedRole.roleId);

                    hideRoleLevelDialog();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Adding Member to Role");
                });
        }
    }

    async function onAddUserToUserGroup() {
        if (!selectedUser) {
            const message = "You must first select a User from the Users list";
            showInformationMessage(ModalMessageType.Information, "Missing Information", message);
            return;
        }

        if (!selectedUserGroup) {
            const message = "You must first select a User Group from the User Groups list";
            showInformationMessage(ModalMessageType.Information, "Missing Information", message);
            return;
        }

        if (selectedUser.userId && selectedUserGroup.userGroupId) {
            await addUserToUserGroup(
                selectedUser.userId,
                selectedUserGroup.userGroupId)
                .then(() => {
                    showToast("User added to user group", "success");

                    if (selectedUserGroup) {
                        getUserGroupUsers(selectedUserGroup.userGroupId);
                    };

                    getUsers();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Adding User to Group");
                });
        }
    }

    //deleting
    async function removeUserFromGroup() {
        if (!selectedUserGroupMember || !selectedUserGroup)
            return;

        var userId = selectedUserGroupMember?.userId;
        var userGroupId = selectedUserGroup.userGroupId;

        await CrudFunctions.deleteData(`common/removeuserfromgroup/${userId}/${userGroupId}`)
            .then(() => {
                showToast("User removed from user group", "success");

                if (selectedUserGroup)
                    getUserGroupUsers(selectedUserGroup.userGroupId);

                getUsers();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Removing User from Group");
            });
    }

    async function removeMemberFromRole() {
        if (!selectedRoleMember)
            return;

        var memberId = selectedRoleMember.memberId;
        var roleId = selectedRoleMember.roleId;

        await CrudFunctions.deleteData(`common/removerolemembership/${memberId}/${roleId}`)
            .then(() => {
                showToast("Member removed from role", "success");

                if (selectedRole)
                    getRoleMembers(selectedRole.roleId);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error removing Member from Role");
            });
    }

    async function onUserGroupDeleting(e: any) {
        if (!selectedUserGroup)
            return;

        var userGroupId = selectedUserGroup.userGroupId;

        await CrudFunctions.deleteData(`common/deleteusergroup/${userGroupId}`)
            .then(() => {
                getUserGroups();
                setSelectedUserGroup(null);
                setSelectedUserGroupMember(null);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Deleting User Group");
            });
    }

    //validating

    async function onUserValidating(e: any) {
        if (e.oldData) {
            let user = e.oldData as User;

            if (user) {
                let updatedUser = { ...user, ...e.newData };
                updateUser(updatedUser)
                    .then(data => {
                        getUsers();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating User");
                    });
            }
        }
        else {
            if (e.newData) {
                const userId = new Guid().toString();
                let user = e.newData as User;
                user.userId = userId;

                if (user) {
                    await insertUser(user)
                        .then(() => {
                            getUsers();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting User");
                        });
                }
            }
        }
    }

    async function rolesValidating(e: any) {
        if (e.oldData) {
            let role = e.oldData as Role;

            if (role) {
                let updatedRole = { ...role, ...e.newData };
                updateRole(updatedRole)
                    .then(() => {
                        getRoles();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Role");
                    });
            }
        }
    }

    async function onUserGroupValidating(e: any) {
        if (e.oldData) {
            let userGroup = e.oldData as UserGroup;

            if (userGroup) {
                let updatedUserGroup = { ...userGroup, ...e.newData };
                await updateUserGroup(updatedUserGroup)
                    .then(data => {
                        getUserGroups();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating User Group");
                    });
            }
        }
        else {
            if (e.newData) {
                let userGroup = e.newData as UserGroup;

                if (userGroup) {
                    await insertUserGroup(userGroup)
                        .then(data => {
                            getUserGroups();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting User Group");
                        });
                }
            }
        }
    }

    // GET functions

    async function getUsers() {
        CrudFunctions.getData("common/allusers/true")
            .then(data => {
                setUsers(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Users");
            });
    }

    async function getUserGroups() {
        CrudFunctions.getData("common/usergroups")
            .then(data => {
                if (data && data.message && data.message === "Unauthorized")
                    return;

                setUserGroups(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving User Groups");
            });
    }

    async function getUserGroupUsers(userGroupId: string) {
        CrudFunctions.getData(`common/usergroupusers/${userGroupId}`)
            .then(data => {
                setUserGroupUsers(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving User Group Users");
            });
    }

    async function getRoles() {
        CrudFunctions.getData("common/roles")
            .then(data => {
                if (data && data.message && data.message === "Unauthorized") {
                    return;
                }

                setRoles(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Roles");
            });
    }

    async function getRoleMembers(roleId: string) {
        CrudFunctions.getData(`common/rolememberships/${roleId}`)
            .then(data => {
                setRoleMembers(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Role Members");
            });
    }

    async function reloadUsersAtServer() {
        CrudFunctions.getData("common/reloadusers")
            .then(data => {
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Reloading Users at Server");
            });
    }

    // POST/PUT functions
    async function insertUser(user: User) {
        return await CrudFunctions.postData("common/user", user);
    }

    async function updateUser(user: User) {
        return await CrudFunctions.putData("common/user", user);
    }

    async function insertUserGroup(userGroup: UserGroup) {
        return await CrudFunctions.postData("common/usergroup", userGroup);
    }

    async function updateUserGroup(userGroup: UserGroup) {
        return await CrudFunctions.putData("common/usergroup", userGroup);
    }

    async function updateRole(role: Role) {
        return await CrudFunctions.putData("common/role", role);
    }

    async function addUserToUserGroup(userId: string, userGroupId: string) {
        return await CrudFunctions.postData(`common/addusertogroup/${userId}/${userGroupId}`, null);
    }

    async function addMemberToRole(memberRole: MemberRole) {
        return await CrudFunctions.postData("common/addmembertorole", memberRole);
    }

    //rendering

    function emailCellRender(e: any) {
        let user = e.data as User;
        let mailToEmail = `mailto:${user.email}`;

        if (user) {
            if (user.email)
                return <a href={mailToEmail}>{user.email}</a>;
            else
                return <div></div>;
        }
    }

    function userGroupUsersRender(user: User) {
        let name = user.userName;
                
        if (user.fullName)
            name = `${user.fullName} (${name})`;
        else
            name = `${user.firstName} ${user.lastName} (${name})`;
            
        return (
            <div>
                <div>{name}</div>
            </div>
        );
    }

    function roleMembersRender(memberRole: MemberRole) {
        switch (memberRole.roleLevel) {
            case RoleLevelType.Viewer:
                return <div><i className="fas fa-binoculars pw-margin-right-05"></i> {memberRole.memberName}</div>;
            case RoleLevelType.Tech:
                return <div><i className="fas fa-user pw-margin-right-05"></i> {memberRole.memberName}</div>;
            case RoleLevelType.TeamLead:
                return <div><i className="fas fa-user-check pw-margin-right-05"></i> {memberRole.memberName}</div>;
            case RoleLevelType.Admin:
                return <div><i className="fas fa-user-tie pw-margin-right-05"></i> {memberRole.memberName}</div>;
            default:
                return <div></div>;
        }
    }

    //navigation
    function goBack() {
        history.back();
    }

    //show / hide

    function showTicketLogEntryDialog() {
        //todo
    }


    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function showRoleLevelDialog() {
        setRoleLevelDialogVisible(true);
    }

    function hideRoleLevelDialog() {
        setRoleLevelDialogVisible(false);
    }

    function showQuickAddNewUsersDialog() {
        setQuickAddNewUsersDialogVisible(true);
    }

    function hideQuickAddNewUsersDialog() {
        setQuickAddNewUsersDialogVisible(false);
    }

    async function getAllData() {
        await Promise.all([
            getRoles(),
            getUsers(),
            getUserGroups()
        ]);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function showInformationMessage(mode: ModalMessageType, title: string, message: string) {
        let mm: ModalMetadata = {
            error: null,
            mode: mode,
            title: title,
            message: message
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}

